import { HouseholdData, OnlineRegFamilies } from './household-data.model';
import { System } from './../../shared-components/System';
import { HouseholdHealth } from './health-update/household-health.model';
import { HouseholdCustomForm } from './custom-form/household-custom-forms.model';
import { Injectable, EventEmitter } from '@angular/core';
import { UserService } from '../../shared-components/user/user.service';
import { YearService } from '../../shared-components/year/year.service';
import { HttpClient } from '@angular/common/http';
import { StudentService } from '../../shared-components/student/student.service';
import { LoginService } from '../../shared-modules/login/login.service';
import { Observable, forkJoin } from 'rxjs';
import { FormPacket } from './form-packet.model';
import { HouseholdParents } from './parent-update/household-parents.model';
import { Form } from './form.model';
import { FamilyDataField } from './family-data-field.model';
import { HouseholdAddresses } from './address-update/householdAddresses';
import { Router, NavigationCancel, RouterStateSnapshot } from '@angular/router';
import { HouseholdStudents } from './student-update/household-students';
import { StudentDetails, StudentLinkedToContact } from './student-update/student-details';
import { map } from '../../../../node_modules/rxjs/operators';
import { Attachment } from '../../shared-components/models/attachment.model';
import { EmergencyContact } from './contact-update/emergency-contacts.model';
import { ErrorList } from '../../shared-components/controls/error-prompt/error-list.model';
import { Year } from '../../shared-components/year/year.model';
import { Address } from '../../shared-components/models/address';

@Injectable()
export class OnlineRegistrationService {

    // Private Properties
    public familyId: number;
    public parentType: string;
    public isMultipleFamiliesPopupOpened: boolean;
    private originalForm: Form;
    private originalFormPacket: FormPacket;
    private _currentForm: Form;
    private _currentFormPacket: FormPacket;
    private _formStatus: boolean;
    private _calledFromReviewSubmission: boolean;

    // Public Properties
    public returnFromLastForm = false;
    public formChange = new EventEmitter<Form>();
    public formPacketChange = new EventEmitter<FormPacket>();
    public ORYear: Year;
    public forcefullyRedirectOnlineRegistration: boolean;

    public clickEvent = 0;

    public studentToContactLinked: StudentLinkedToContact[] = [];

    // Class Functions
    constructor(
        private userService: UserService,
        private yearService: YearService,
        private studentService: StudentService,
        private loginService: LoginService,
        private http: HttpClient,
        private router: Router
    ) {

    }

    get currentForm() {
        return this._currentForm;
    }

    set currentForm(value: Form) {
        this._currentForm = value;
        this.formChange.emit(this._currentForm);
    }

    get currentFormPacket() {
        return this._currentFormPacket;
    }

    set currentFormPacket(value: FormPacket) {
        this._currentFormPacket = value;
        this.formPacketChange.emit(this._currentFormPacket);
    }
   get calledFromReviewSubmission()
   {
       return this._calledFromReviewSubmission;
   }
   set calledFromReviewSubmission(value: boolean)
   {
       this._calledFromReviewSubmission = value;
   }
    get formStatus(): boolean {
        return this._formStatus;
    }

    get V10Data(): boolean {
        if (this.isOnlineRegistration()) {
            return this.loginService.LoginSettings.ParentPortalORv10Data;
        } else {
            return this.loginService.LoginSettings.ParentPortalUHDv10Data;
        }
    }

    get isLastForm(): boolean {
        // POST: returns true if this is the last form
        return this.getCurrentFormIndex() == this.getForms(this.currentFormPacket).length - 1;
    }

    // Methods
    isOnlineRegistration() {
        return this.router.url.toLowerCase().includes('onlineregistration') || this.calledFromReviewSubmission  || this.userService.currentUser.NewFamilyRegistration || !!this.forcefullyRedirectOnlineRegistration;
    }

    isFieldRequired(householdData: HouseholdData, requiredProperty: string | boolean) {
        if (typeof requiredProperty == 'string') {
            return householdData[requiredProperty] && (!this.isOnlineRegistration() || householdData.Status);
        } else {
            return requiredProperty && (!this.isOnlineRegistration() || householdData.Status);
        }
    }

    getFieldPhoneRegexp(householdData: HouseholdData) {
        if (this.isOnlineRegistration() && householdData && !householdData.Status) {
            return /.*/;
        } else {
            return System.phoneRegexp;
        }
    }

    getFieldEmailRegexp(householdData: HouseholdData) {
        if (this.isOnlineRegistration() && householdData && !householdData.Status) {
            return /.*/;
        } else {
            return System.mailRegexp;
        }
    }

    getFieldZipRegexp(householdData: HouseholdData) {
        if (this.isOnlineRegistration() && householdData && !householdData.Status) {
            return /.*/;
        } else {
            return System.zipRegexp;
        }
    }

    getCurrentFormIndex(): number {
        // PRE: the current form and the current form packet must be set. Otherwise, returns -1.
        // POST: returns the index of the current form
        if (!this.currentForm || !this.currentFormPacket) {
            return -1;
        } else {
            return this.getForms(this.currentFormPacket).indexOf(this.currentForm);
        }
    }

    getFormCount(): number {
        // PRE: the form packet must be set
        // POST: reutnrs the number of forms in the packet
        return this.getForms(this.currentFormPacket).length;
    }

    getFormAtIndex(index: number): Form {
        // PRE: the current form and the current form packet must be set. Otherwise, returns null.
        // POST: returns the form at the given index
        if (!this.currentForm || !this.currentFormPacket || this.getForms(this.currentFormPacket).length <= index) {
            return null;
        } else {
            return this.getForms(this.currentFormPacket)[index];
        }
    }

    routeToForm(form: Form, formPacket: FormPacket) {
        // PRE: a form to route to amongst a list of form packet
        // POST: routes to that form and sets the appropriate variable

        // If the form or the formpacket are not there, don't route
        if (!form || !formPacket) {
            return;
        }

        // Setting up data for route
        this.originalForm = this.currentForm;
        this.originalFormPacket = new FormPacket();
        this.originalFormPacket = this.currentFormPacket;
        this.currentForm = form;
        this.currentFormPacket = new FormPacket();
        this.currentFormPacket = formPacket;
        this._formStatus = this.currentFormPacket.Status || this.currentForm.FormStatus == 'Approved';

        // Performing route
        if (this.isOnlineRegistration()) {
            if ((form.ScreenName.includes('Household Parents') || form.ScreenName.includes('Parent/Contact Information')) && (this.V10Data == true || form.StudentId == 0)) {
                this.router.navigateByUrl('/student360/onlineRegistration/householdParents');
            } else if (form.ScreenName.includes('Household Addresses') && form.ScreenName.length == 19 && (this.V10Data == true || form.StudentId == 0)) {
                this.router.navigateByUrl('/student360/onlineRegistration/householdAddresses');
            } else if (form.ScreenName.includes('Addresses') && this.V10Data == true && form.ScreenName.length == 9) {
                this.router.navigateByUrl('/student360/onlineRegistration/householdAddresses');
            } else if (form.ScreenName.includes('Student Information') && form.ScreenName.length == 19 && (this.V10Data == true || form.StudentId == 0)) {
                this.router.navigateByUrl('/student360/onlineRegistration/student');
            } else if (form.ScreenName.includes('Emergency Contacts') && form.ScreenName.length == 18 && (this.V10Data == true || form.StudentId == 0)) {
                this.router.navigateByUrl('/student360/onlineRegistration/emergencyContacts');
            } else if (form.ScreenName.includes('Non-resident Parents') && form.ScreenName.length == 20 && (this.V10Data == true || form.StudentId == 0)) {
                this.router.navigateByUrl('/student360/onlineRegistration/nonresidentParents');
            } else {
                this.router.navigateByUrl('/student360/onlineRegistration/customForm?formName=' + this.currentForm.ScreenName);
            }
        } else {
            if (form.ScreenName.includes('Household Parents') || form.ScreenName.includes('Parent/Contact Information')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/householdParents');
            } else if (form.ScreenName.includes('Household Addresses')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/householdAddresses');
            } else if (form.ScreenName.includes('Addresses') && this.V10Data == true) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/householdAddresses');
            } else if (form.ScreenName.includes('Student Information')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/student');
            } else if (form.ScreenName.includes('Health/Medical')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/healthMedical');
            } else if (form.ScreenName.includes('Emergency Contacts')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/emergencyContacts');
            } else if (form.ScreenName.includes('Non-resident Parents')) {
                this.router.navigateByUrl('/student360/parentPortalDataUpdate/nonresidentParents');
            }
        }

        // If the route is canceled, reset data
        this.router.events.subscribe(e => {
            if (e instanceof NavigationCancel && this.originalForm && this.originalFormPacket) {
                this.currentForm = this.originalForm;
                this.currentFormPacket = this.originalFormPacket;
                this._formStatus = this.currentFormPacket.Status || this.currentForm.FormStatus == 'Approved';
            }
        });
    }

    guardCheck(
        data: HouseholdData,
        dirty = false,
        nextState?: RouterStateSnapshot,
        errors: ErrorList[] = []
    ): number {
        // PRE: the data, whether the form is dirty, the next state, and a list of errors
        // POST: this is the call that checks what the guard should do

        // If this is online registration and the next URL is OR or PPU, perform a special check
        if (nextState && this.isOnlineRegistration() &&
            data &&
            (nextState.url.includes('onlineRegistration') || nextState.url.includes('parentPortalDataUpdate'))) {

            // If dirty ...
            if (dirty) {

                // And there are no errors ...
                if (errors.length == 0) {

                    // Autosave with waiting
                    return 5;
                }

                // And there are errors ...
                else {

                    // Prevent route entirely
                    return -1;
                }
            }

            // If not dirty, just continue
            else {
                return 2;
            }
        }

        // If dirty, show warning. Otherwise, just continue.
        if (dirty) {
            return 4;
        } else {
            return 2;
        }
    }

    getOnlineRegFamilies(isUHD: boolean): Observable<OnlineRegFamilies[]> {
        const queryString = '?userId=' + this.userService.currentUser.Id +
            '&yearId=' + this.yearService.student360Year.Id +
            '&isUHD=' + isUHD;
        return this.http.get<OnlineRegFamilies[]>(System.apiURL.Student + 'GetOnlineRegistrationFamilies' + queryString);
    }

    // Shared Server Calls
    getFormPacket(allowUserToAddStudent: boolean = false, familyId: number = 0, parentType: string = '', userId: number = this.userService.currentUser.Id, student360YearId: number = 0, review: boolean = false): Observable<FormPacket> {
        // PRE: online registration is true if this is for Online Registration
        // POST: gets the user's form packet for the specified update page

        if (this.isOnlineRegistration()) {
           // let student360YearId: number;
            if (student360YearId == 0 && this.forcefullyRedirectOnlineRegistration == true) {
                student360YearId = this.ORYear.Id;
            }
            else if (student360YearId == 0 ) {
                student360YearId = this.yearService.student360Year == undefined ? this.ORYear.Id : this.yearService.student360Year.Id;
            }

            const queryString = '?userId=' + userId +
                '&yearId=' + student360YearId +
                '&languageId=' + this.userService.languageId +
                '&allowUserToAddStudent=' + allowUserToAddStudent +
                '&familyId=' + familyId +
                '&parentType=' + parentType + 
                '&review=' + review;
            return this.http.get<FormPacket>(System.apiURL.Student + 'GetOnlineRegistrationUserPacket' + queryString).pipe(map(formPacket => {
                this.currentFormPacket = System.convert<FormPacket>(formPacket, new FormPacket());
                return formPacket;
            }));
        } else {
            const queryString = '?userId=' + this.userService.currentUser.Id +
                '&studentId=' + this.studentService.selectedStudent.Id +
                '&yearId=' + this.yearService.student360Year.Id +	// We don't use OR Year here because UHD doesn't allow year selection
                '&familyId=' + familyId +
                '&parentType=' + parentType;
            return this.http.get<FormPacket>(System.apiURL.Student + 'GetParentPortalDataUpdateUserPacket' + queryString).pipe(map(formPacket => {
                this.currentFormPacket = System.convert<FormPacket>(formPacket, new FormPacket());
                return formPacket;
            }));
        }
    }

    getFamilyDataFields(): Observable<FamilyDataField[]> {
        // POST: obtains the family data fields for parent update
        return this.http.get<FamilyDataField[]>(System.apiURL.Student + 'GetFamilyDataFields');
    }

    getHouseholdParents(): Observable<HouseholdParents> {
        // PRE: the online registration currentForm must be the form for which we are obtaining data for
        // POST: gets the household parents for online registration
        const familyId = (!this.V10Data && this.familyId != null && this.familyId > 0) ? this.familyId : this.userService.currentUser.FamilyId;
        if (this.isOnlineRegistration()) {
            const queryString = '?familyId=' + familyId +
                '&yearId=' + this.currentFormPacket.YearId +
                '&onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId +
                '&languageId=' + this.userService.languageId +
                '&ayIdentifier=' + this.currentFormPacket.YearDescription;
            return this.http.get<HouseholdParents>(System.apiURL.Student + 'GetOnlineRegistrationHouseholdParents' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<HouseholdParents>(System.apiURL.Student + 'GetParentPortalDataUpdateHouseholdParents' + queryString);
        }
    }

    saveHouseholdParents(householdParents: HouseholdParents, newContact: boolean): Observable<any> {
        // POST: saves the household parents to the server
        if (this.isOnlineRegistration()) {
            const queryString = '?userId=' + this.userService.currentUser.Id +
                '&newContact=' + (newContact ? 1 : 0);
            return this.http.post(System.apiURL.Student + 'SaveOnlineRegistrationHouseholdParentData' + queryString, householdParents);
        } else {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveParentPortalDataUpdateHouseholdParentData' + queryString, householdParents);
        }
    }

    deleteHouseholdParents(): Observable<any> {
        // PRE: the online registration currentForm must be the form for which we are deleting parent data
        // POST: deletes the new values from the parent data
        if (this.isOnlineRegistration()) {
            const queryString = '?onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&userId=' + this.userService.currentUser.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationHouseholdParentData' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteParentPortalDataUpdateHouseholdParentData' + queryString);
        }
    }

    getHouseholdAddresses(): Observable<HouseholdAddresses> {
        // POST: obtains the household addresses
        if (this.isOnlineRegistration()) {
            const queryString = '?familyId=' + this.currentFormPacket.FamilyId +
                '&yearId=' + this.currentFormPacket.YearId +
                '&onlineRegistrationLInkScreenId=' + this.currentForm.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId +
                '&languageId=' + this.userService.languageId +
                '&ayIdentifier=' + this.currentFormPacket.YearDescription;
            return this.http.get<HouseholdAddresses>(System.apiURL.Student + 'GetOnlineRegistrationHouseholdAddresses' + queryString).pipe(
                map(results => {
                    for (let i = 0; i < results.OnlineRegistrationHouseholdAddressesDetail.length; i++) {
                        const address = results.OnlineRegistrationHouseholdAddressesDetail[i];
                        results.OnlineRegistrationHouseholdAddressesDetail[i] = System.convert<Address>(address, new Address());
                    }
                    return results;
                })
            );
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<HouseholdAddresses>(System.apiURL.Student + 'GetParentPortalDataUpdateHouseholdAddresses' + queryString).pipe(
                map(results => {
                    for (let i = 0; i < results.ParentPortalDataUpdateHouseholdAddressesDetail.length; i++) {
                        const address = results.ParentPortalDataUpdateHouseholdAddressesDetail[i];
                        results.ParentPortalDataUpdateHouseholdAddressesDetail[i] = System.convert<Address>(address, new Address());
                    }
                    return results;
                })
            );
        }
    }

    saveHouseholdAddresses(householdAddresses: HouseholdAddresses): Observable<any> {
        // POST: saves the household addresses to the server
        if (this.isOnlineRegistration()) {
            if (householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].SameAsPrimaryResidence) {
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressLine1New = '';
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressLine2New = '';
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressLine3New = '';
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressCityNew = '';
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressStateNew = '';
                householdAddresses.OnlineRegistrationHouseholdAddressesDetail[0].POBoxAddressZipNew = '';
            }
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveOnlineRegistrationHouseholdAddressData' + queryString, householdAddresses);
        } else {
            if (householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].SameAsPrimaryResidence) {
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressLine1New = '';
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressLine2New = '';
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressLine3New = '';
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressCityNew = '';
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressStateNew = '';
                householdAddresses.ParentPortalDataUpdateHouseholdAddressesDetail[0].POBoxAddressZipNew = '';
            }
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveParentPortalDataUpdateHouseholdAddressData' + queryString, householdAddresses);
        }
    }

    deleteHouseholdAddresses(): Observable<any> {
        // PRE: the online registration currentForm must be the form for which we are deleting address data
        // POST: deletes the new values from the address data
        if (this.isOnlineRegistration()) {
            const queryString = '?onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&userId=' + this.userService.currentUser.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationHouseholdAddressData' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteParentPortalDataUpdateHouseholdAddressData' + queryString);
        }
    }

    getHouseholdStudents(): Observable<HouseholdStudents> {
        // POST: obtains the household students
        const familyId = (!this.V10Data && this.familyId != null && this.familyId > 0) ? this.familyId : this.userService.currentUser.FamilyId;
        let parentType = '';
        if (familyId > 0) {
            parentType = this.parentType;
        }
        const memId = this.V10Data ? this.userService.currentUser.MemberId : this.userService.currentUser.Id;
        if (this.isOnlineRegistration()) {
            const queryString = '?familyId=' + familyId +
                '&yearId=' + this.currentFormPacket.YearId +
                '&onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&currentUserMemberId=' + memId + // Pass User Id For V9
                '&languageId=' + this.userService.languageId +
                '&ayIdentifier=' + this.currentFormPacket.YearDescription +
                '&parentType=' + parentType;
            return this.http.get<HouseholdStudents>(System.apiURL.Student + 'GetOnlineRegistrationStudent' + queryString);
        } else {
            const queryString = '?familyId=' + familyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId +
                '&yearId=' + this.yearService.student360Year.Id +
                '&userId=' + this.userService.currentUser.Id +
                '&parentType=' + parentType;
            return this.http.get<HouseholdStudents>(System.apiURL.Student + 'GetParentPortalDataUpdateStudent' + queryString);
        }
    }

    saveHouseholdStudents(householdStudents: HouseholdStudents): Observable<any> {
        // POST: saves the household students to the server
        if (this.isOnlineRegistration()) {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveOnlineRegistrationStudentData' + queryString, householdStudents);
        } else {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveParentPortalDataUpdateStudentData' + queryString, householdStudents);
        }
    }

    deleteHouseholdStudents(): Observable<any> {
        // PRE: the online registration currentForm must be the form for which we are deleting address data
        // POST: deletes the new values from the address data
        if (this.isOnlineRegistration()) {
            const queryString = '?onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&userId=' + this.userService.currentUser.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationStudentData' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteParentPortalDataUpdateStudentData' + queryString);
        }
    }

    getMatchingStudentAddresses(students: HouseholdStudents): Observable<StudentDetails[]> {
        // PRE: an edited household students
        // POST: returns the matching student addresses
        if (this.isOnlineRegistration()) {
            return this.http.post<StudentDetails[]>(System.apiURL.Student + 'ORGetMatchingStudentAddresses', students);
        } else {
            return this.http.post<StudentDetails[]>(System.apiURL.Student + 'PPUGetMatchingStudentAddresses', students);
        }
    }

    getMatchingParentAddresses(students: HouseholdStudents): Observable<HouseholdAddresses[]> {
        // PRE: an edited household students
        // POST: returns the matching parent addresses
        if (this.isOnlineRegistration()) {
            return this.http.post<HouseholdAddresses[]>(System.apiURL.Student + 'ORGetMatchingParentAddresses', students);
        } else {
            return this.http.post<HouseholdAddresses[]>(System.apiURL.Student + 'PPUGetMatchingParentAddresses', students);
        }
    }

    getMatchingAddresses(students: HouseholdStudents): Observable<any> {
        // PRE: an edited household students
        // POST: returns the matching student address and the matching parent addresses as an array
        //      results[0] is students, results[1] is parents
        return forkJoin(this.getMatchingStudentAddresses(students), this.getMatchingParentAddresses(students));
    }

    syncStudentAddresses(students: StudentDetails[]): Observable<any> {
        // PRE: addresses that belong to students
        // POST: syncs them on the database
        if (this.isOnlineRegistration()) {
            const queryString = '?memberId=' + this.userService.currentUser.MemberId;
            return this.http.post<any>(System.apiURL.Student + 'ORStudentSyncStudentAddresses' + queryString, students);
        } else {
            const queryString = '?memberId=' + this.userService.currentUser.MemberId;
            return this.http.post<any>(System.apiURL.Student + 'PPUStudentSyncStudentAddresses' + queryString, students);
        }
    }

    syncParentAddresses(parentAddresses: HouseholdAddresses[]): Observable<any> {
        // PRE: addresses that belong to parents
        // POST: syncs them on the database
        if (this.isOnlineRegistration()) {
            const queryString = '?memberId=' + this.userService.currentUser.MemberId;
            return this.http.post<any>(System.apiURL.Student + 'ORStudentSyncParentAddresses' + queryString, parentAddresses);
        } else {
            const queryString = '?memberId=' + this.userService.currentUser.MemberId;
            return this.http.post<any>(System.apiURL.Student + 'PPUStudentSyncParentAddresses' + queryString, parentAddresses);
        }
    }

    syncAddresses(students: StudentDetails[], parentAddresses: HouseholdAddresses[]): Observable<any> {
        // POST: syncs the addresses on the database, respectively
        return forkJoin(this.syncStudentAddresses(students), this.syncParentAddresses(parentAddresses));
    }

    getEmergencyContact(): Observable<EmergencyContact> {
        // PRE: the online registration currentForm must be the form for which we are obtaining data for
        // POST: gets the emergency contactsts for online registration
        const familyId = (!this.V10Data && this.familyId != null && this.familyId > 0) ? this.familyId : this.userService.currentUser.FamilyId;
        if (this.isOnlineRegistration()) {
            const queryString = '?familyId=' + familyId +
                '&yearId=' + this.currentFormPacket.YearId +
                '&onlineRegistrationLinkScreenId=' + this.currentForm.Id;
            return this.http.get<EmergencyContact>(System.apiURL.Student + 'GetOnlineRegistrationEmergencyContacts' + queryString);
        }
        else {
            const queryString = '?familyId=' + familyId +
                '&yearId=' + this.yearService.student360Year.Id +
                '&onlineRegistrationLinkScreenId=' + this.currentForm.Id;
            return this.http.get<EmergencyContact>(System.apiURL.Student + 'GetParentPortalDataUpdateEmergencyContacts' + queryString);
        }
    }

    saveEmergencyContact(data: EmergencyContact, newContact: boolean): Observable<any> {
        // POST: saves the emergency contact to the server
        if (this.isOnlineRegistration()) {
            const queryString = '?userId=' + this.userService.currentUser.Id +
                '&newContact=' + (newContact ? 1 : 0);
            return this.http.post(System.apiURL.Student + 'SaveOnlineRegistrationEmergencyContactData' + queryString, data);
        } else {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveParentPortalDataUpdateEmergencyContactData' + queryString, data);
        }
    }

    deleteEmergencyContact(): Observable<any> {
        // PRE: the online registration currentForm must be the form for which we are deleting contact data
        // POST: deletes the new values from the emergency contact data
        if (this.isOnlineRegistration()) {
            const queryString = '?onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&userId=' + this.userService.currentUser.Id +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationEmergencyContactData' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId +
                '&currentUserMemberId=' + this.userService.currentUser.MemberId;
            return this.http.get<any>(System.apiURL.Student + 'deleteParentPortalDataUpdateEmergencyContactData' + queryString);
        }
    }

    getNonResidentParents(): Observable<HouseholdParents> {
        const familyId = (!this.V10Data && this.familyId != null && this.familyId > 0) ? this.familyId : this.userService.currentUser.FamilyId;
        if (this.isOnlineRegistration()) {
            const queryString = '?familyId=' + familyId +
                '&yearId=' + this.currentFormPacket.YearId +
                '&onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&parentType=' + this.parentType +
                '&userId=' + this.userService.currentUser.Id
                ;
            return this.http.get<HouseholdParents>(System.apiURL.Student + 'GetOnlineRegistrationNonResidentParents' + queryString);
        } else {
            const queryString = '?familyId=' + familyId +
                '&userId=' + this.userService.currentUser.Id;
            return this.http.get<HouseholdParents>(System.apiURL.Student + 'GetParentPortalDataUpdateNonResidentParents' + queryString);
        }
    }

    saveOnlineRegistrationNonResidentParentData(householdParents: HouseholdParents, newContact: boolean): Observable<any> {
        if (this.isOnlineRegistration()) {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveOnlineRegistrationNonResidentParentData' + queryString, householdParents);
        } else {
            const queryString = '?userId=' + this.userService.currentUser.Id;
            return this.http.post(System.apiURL.Student + 'SaveParentPortalDataUpdateNonResidentParentData' + queryString, householdParents);
        }
    }

    deleteNonResidentParentData(): Observable<any> {
        if (this.isOnlineRegistration()) {
            const queryString = '?onlineRegistrationLinkScreenId=' + this.currentForm.Id +
                '&userId=' + this.userService.currentUser.Id;
            return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationNonResidentParentData' + queryString);
        } else {
            const queryString = '?familyId=' + this.userService.currentUser.FamilyId;
            return this.http.get<any>(System.apiURL.Student + 'DeleteParentPortalDataUpdateNonResidentParentData' + queryString);
        }
    }

    // Online Registration Only Server Calls
    getCustomForm(): Observable<HouseholdCustomForm> {
        // POST: obtains the custom forms for a household
        const familyId = (!this.V10Data && this.familyId != null && this.familyId > 0) ? this.familyId : this.userService.currentUser.FamilyId;
        const queryString = '?familyId=' + familyId +
            '&yearId=' + this.currentFormPacket.YearId +
            '&onlineRegistrationLinkScreenId=' + this.currentForm.Id +
            '&currentUserMemberId=' + this.userService.currentUser.MemberId +
            '&languageId=' + this.userService.languageId +
            '&ayIdentifier=' + this.currentFormPacket.YearDescription;
        return this.http.get<HouseholdCustomForm>(System.apiURL.Student + 'GetOnlineRegistrationCustomForm' + queryString);
    }

    saveCustomForm(customForm: HouseholdCustomForm): Observable<any> {
        // PRE: a custom form to save
        // POST: saves the custom form to the server
        const queryString = '?userId=' + this.userService.currentUser.Id;
        return this.http.post<any>(System.apiURL.Student + 'SaveOnlineRegistrationCustomFormData' + queryString, customForm);
    }

    deleteCustomForm(): Observable<any> {
        // PRE: the current form is the one we're resetting
        // POST: resets the current form in the server
        const queryString = '?onlineregistrationLinkScreenId=' + this.currentForm.Id +
            '&userId=' + this.userService.currentUser.Id +
            '&currentUserMemberId=' + this.userService.currentUser.MemberId;
        return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationCustomFormData' + queryString);
    }

    submitOnlineRegistrationPacket(): Observable<any> {
        // PRE: the data for all packets should be set
        // POST: sends the submit event to the server
        // NOTE: this has no data because all the data should be saved on the server
        const queryString = '?Id=' + this.currentFormPacket.Id +
            '&userId=' + this.userService.currentUser.Id +
            '&currentUserMemberId=' + this.userService.currentUser.MemberId +
            '&languageId=' + this.userService.languageId;
        return this.http.get<any>(System.apiURL.Student + 'SubmitOnlineRegistrationPacket' + queryString);
    }

    // Parent Portal Only Server Calls
    getHouseholdHealth(): Observable<HouseholdHealth> {
        // POST: obtains the current medical information for students
        const queryString = '?currentUserMemberId=' + this.userService.currentUser.MemberId +
            '&yearId=' + this.yearService.student360Year.Id;
        return this.http.get<HouseholdHealth>(System.apiURL.Student + 'GetParentPortalDataUpdateHealthMedical' + queryString);
    }

    saveHouseholdHealth(householdHealth: HouseholdHealth): Observable<any> {
        // PRE: an edited household health object
        // POST: saves the data to the server
        const queryString = '?userId=' + this.userService.currentUser.Id +
            '&yearId=' + this.yearService.student360Year.Id;
        return this.http.post<any>(System.apiURL.Student + 'SaveParentPortalDataUpdateHealthMedicalData' + queryString, householdHealth);
    }

    deleteHouseholdHealth(): Observable<any> {
        // POST: resets the household health
        const queryString = '?currentUserMemberId=' + this.userService.currentUser.MemberId;
        return this.http.get<any>(System.apiURL.Student + 'DeleteParentPortalDataUpdateHealthMedicalData' + queryString);
    }

    saveAttachment(attachment: Attachment): Observable<any> {
        // PRE: an attachment with an attached user file
        // POST: saves the attachment to the server
        return this.http.post<any>(System.apiURL.Student + 'SaveOnlineRegistrationAttachment', attachment);
    }

    deleteAttachment(attachment: Attachment): Observable<any> {
        // PRE: an attachment to delete
        // POST: deletes the attachment from the server
        const queryString = '?DocumentId=' + attachment.DocumentFileId +
            '&memberId=' + this.userService.currentUser.MemberId;
        return this.http.get<any>(System.apiURL.Student + 'DeleteOnlineRegistrationAttachment' + queryString);
    }

    checkStudentImmunizationCompliance(): Observable<string[]> {
        // POST: returns the students who aren't complying and the corresponding messages
        const queryString = '?userId=' + this.userService.currentUser.Id +
            '&yearId=' + this.yearService.student360Year.Id +
            '&currentUserMemberid=' + this.userService.currentUser.MemberId +
            '&languageId=' + this.userService.languageId +
            '&ayIdentifier=' + this.yearService.student360Year.AYIdentifier;
        return this.http.get<string[]>(System.apiURL.Student + 'CheckORStudentsImmunizationCompliance' + queryString);
    }

    getForms(packet: FormPacket) {
        // PRE: online registration is true if this is for Online Registration
        // POST: returns the formPacket for the given use case
        let activeStudents: number[] = [];
        if (packet && packet.OnlineRegistrationActiveStudents) {
            activeStudents = packet.OnlineRegistrationActiveStudents.filter(activeStudent => this.V10Data || ((this.parentType == 'Nonresident Parent' && activeStudent.UserIsPrimaryContact == false) || this.parentType != 'Nonresident Parent')).map(s => s.Id);
        }
        if (this.isOnlineRegistration()) {
            if (packet.OnlineRegistrationForms && packet.OnlineRegistrationForms.length > 0) {
                return packet.OnlineRegistrationForms;
            } else {
                const blankFrom: Form[] = [];
                return blankFrom;
            }

        } else {
            if (packet.ParentPortalDataUpdateForms && packet.ParentPortalDataUpdateForms.length > 0) {
                return packet.ParentPortalDataUpdateForms;
            } else {
                const blankFrom: Form[] = [];
                return blankFrom;
            }
            return packet.ParentPortalDataUpdateForms;
        }
    }

    getOnlineRegistrationYears(allowUserToAddStudent: boolean): Observable<Year[]> {
        const queryString = '?allowUserToAddStudent=' + allowUserToAddStudent;
        return this.http.get<Year[]>(System.apiURL.Student + 'GetOnlineRegistrationYears' + queryString);
    }

    getORYearGradeLevels(yearId: number): Observable<string> {
        const queryString = '?yearId=' + yearId;
        return this.http.get<string>(System.apiURL.Student + 'GetORYearGradeLevels' + queryString);
    }

    deleteOnlineRegistrationUserPacket(userPacketId: number) {
        const queryString = '?id=' + userPacketId;
        return this.http.get(System.apiURL.Student + 'DeleteOnlineRegistrationUserPacket' + queryString);
    }

    getNewStudentLinkedContacts(familyId: number, formId: number): Observable<StudentLinkedToContact[]> {
        const queryString = '?familyId=' + familyId +
        '&formId=' + formId;
        return this.http.get<StudentLinkedToContact[]>(System.apiURL.Student + 'GetNewStudentLinkedContacts' + queryString);
    }

    saveNewStudentLinkedContacts(familyId: number, packetId: number, data: StudentLinkedToContact[]): Observable<boolean> {
        const queryString = '?familyId=' + familyId +
        '&packetId=' + packetId;
        return this.http.post<boolean>(System.apiURL.Student + 'SaveNewStudentLinkedContacts' + queryString, data);
    }
}
